import { Inbox, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { T, tt } from '@agos/shared'

import { cn } from '../cn'

// ─── EmptyState ──────────────────────────────────────────────────────────────

interface EmptyStateProps {
  icon?: ReactNode
  title?: string
  description?: string
  action?: ReactNode
  className?: string
}

export function EmptyState({ icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-16 text-center', className)}>
      <div className="mb-3 text-muted-foreground/40">{icon ?? <Inbox className="w-12 h-12" />}</div>
      {title && <p className="text-sm font-semibold text-foreground mb-1">{title}</p>}
      <p className="text-sm text-muted-foreground max-w-sm">
        {/* FB-006: legacy reads "Ziadne data na zobrazenie" - Slovak with its
            diacritics stripped. sk.json spells it correctly, so the correct
            spelling is what ships, through the translation mechanism. */}
        {description ?? <T>Žiadne dáta na zobrazenie</T>}
      </p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

export type BadgeColor =
  | 'green'
  | 'red'
  | 'amber'
  | 'blue'
  | 'violet'
  | 'zinc'
  | 'orange'
  | 'emerald'
  | 'pink'
  | 'cyan'

/**
 * Fixed palette, ported verbatim. Order status colours are learned by every
 * member of staff — amber means "in preparation" on every screen in the
 * building, and a badge that picks its own colour breaks that.
 */
const BADGE_COLORS: Record<BadgeColor, string> = {
  green: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  emerald: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  red: 'bg-red-500/15 text-red-500 border-red-500/30',
  amber: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  blue: 'bg-blue-500/15 text-blue-500 border-blue-500/30',
  violet: 'bg-violet-500/15 text-violet-500 border-violet-500/30',
  zinc: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  orange: 'bg-orange-500/15 text-orange-500 border-orange-500/30',
  pink: 'bg-pink-500/15 text-pink-500 border-pink-500/30',
  cyan: 'bg-cyan-500/15 text-cyan-500 border-cyan-500/30',
}

interface StatusBadgeProps {
  label: string
  color: BadgeColor
  icon?: LucideIcon
  size?: 'sm' | 'md'
  className?: string
  /**
   * Hover text — §9.177. A badge is two or three words; when those words are a *verdict* they need
   * to be able to say what was compared with what.
   */
  title?: string
}

export function StatusBadge({
  label,
  color,
  icon: Icon,
  size = 'sm',
  title,
  className,
}: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 rounded-full border font-semibold whitespace-nowrap',
        size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
        BADGE_COLORS[color],
        className,
      )}
    >
      {Icon && <Icon className={size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'} />}
      {label}
    </span>
  )
}

// ─── StatCard ────────────────────────────────────────────────────────────────

export type StatColor = 'primary' | 'blue' | 'emerald' | 'amber' | 'red' | 'violet' | 'orange'

const STAT_COLORS: Record<StatColor, { background: string; text: string }> = {
  primary: { background: 'bg-primary/10', text: 'text-primary' },
  blue: { background: 'bg-blue-500/10', text: 'text-blue-500' },
  emerald: { background: 'bg-emerald-500/10', text: 'text-emerald-500' },
  amber: { background: 'bg-amber-500/10', text: 'text-amber-500' },
  red: { background: 'bg-red-500/10', text: 'text-red-500' },
  violet: { background: 'bg-violet-500/10', text: 'text-violet-500' },
  orange: { background: 'bg-orange-500/10', text: 'text-orange-500' },
}

interface StatCardProps {
  label: string
  value: string | number
  sub?: string
  icon?: LucideIcon
  color?: StatColor
  className?: string
  /**
   * A hook for a test that needs this card's figure.
   *
   * Named rather than found by its label: a Slovak label is the thing §8 keeps byte-for-byte, so a
   * test matching on one breaks when a translation is corrected — and the figure is what such a test
   * is after.
   */
  testId?: string
}

export function StatCard({
  label,
  value,
  sub,
  icon: Icon,
  color = 'primary',
  className,
  testId,
}: StatCardProps) {
  const palette = STAT_COLORS[color]
  return (
    <div className={cn('bg-card border border-border rounded-xl p-4', className)} data-stat={testId}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div
            className={cn(
              'w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              palette.background,
            )}
          >
            <Icon className={cn('w-4.5 h-4.5', palette.text)} />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium truncate">
            {label}
          </p>
          <p className={cn('text-lg font-bold leading-tight', palette.text)} data-stat-value>
            {value}
          </p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  )
}

// ─── Spinner ─────────────────────────────────────────────────────────────────

/** The loading ring the auth gate and every slow board show. */
export function Spinner({ className }: { className?: string }) {
  return (
    <div
      role="status"
      aria-label={tt('Načítavam...')}
      className={cn(
        'w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin',
        className,
      )}
    />
  )
}
